import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import { FlowType, ReturnFlow } from "@redotech/redo-model/return-flow";
import { Condition } from "@redotech/redo-model/return-flow/condition";
import {
  ConditionState,
  ConditionType,
  ConditionTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/return-flow/condition";
import { ADJUSTMENT_AMOUNT } from "@redotech/redo-return-ui/flows/return-flow/condition-types/adjustment-amount";
import { AFTER_HOURS } from "@redotech/redo-return-ui/flows/return-flow/condition-types/after-hours";
import { CHANNEL } from "@redotech/redo-return-ui/flows/return-flow/condition-types/channel";
import { COLLECTION } from "@redotech/redo-return-ui/flows/return-flow/condition-types/collection";
import { COMPENSATION_METHOD } from "@redotech/redo-return-ui/flows/return-flow/condition-types/compensation-method";
import { COVERAGE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/coverage";
import { CUSTOMER_ADDRESS } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-address";
import { CUSTOMER_COUNTRY } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-country";
import { CUSTOMER_EMAIL } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-email";
import { CUSTOMER_EMAIL_SUBSCRIBER } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-email-subscriber";
import { CUSTOMER_SMS_SUBSCRIBER } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-sms-subscriber";
import { CUSTOMER_TAG } from "@redotech/redo-return-ui/flows/return-flow/condition-types/customer-tag";
import { DAMAGED } from "@redotech/redo-return-ui/flows/return-flow/condition-types/damaged";
import {
  ORDER_DATE,
  RETURN_DATE,
} from "@redotech/redo-return-ui/flows/return-flow/condition-types/date-conditions";
import { DELIVERY_WITHIN } from "@redotech/redo-return-ui/flows/return-flow/condition-types/delivery-within";
import { DISCOUNT } from "@redotech/redo-return-ui/flows/return-flow/condition-types/discount";
import { EXCHANGE_COUNT } from "@redotech/redo-return-ui/flows/return-flow/condition-types/exchange-count";
import { EXTENDED_WARRANTY } from "@redotech/redo-return-ui/flows/return-flow/condition-types/extended-warranty";
import { EXTENDED_WARRANTY_PURCHASED } from "@redotech/redo-return-ui/flows/return-flow/condition-types/extended-warranty-purchased";
import { FINAL_SALE_RETURN } from "@redotech/redo-return-ui/flows/return-flow/condition-types/final-sale-return";
import { FULFILLMENT_TRACKING_UPDATE_WITHIN } from "@redotech/redo-return-ui/flows/return-flow/condition-types/fulfillment-tracking-update-within";
import { FULFILLMENT_WITHIN } from "@redotech/redo-return-ui/flows/return-flow/condition-types/fulfillment-within";
import { IS_REPAIR } from "@redotech/redo-return-ui/flows/return-flow/condition-types/is-repair";
import { ITEM_DISCOUNT } from "@redotech/redo-return-ui/flows/return-flow/condition-types/item-discount";
import { ITEMS_BEING_RETURNED_COUNT } from "@redotech/redo-return-ui/flows/return-flow/condition-types/items-being-returned-count";
import { LOCATION_NAME } from "@redotech/redo-return-ui/flows/return-flow/condition-types/location-name";
import { LOYALTY_EARNED } from "@redotech/redo-return-ui/flows/return-flow/condition-types/loyalty-earned";
import { MERCHANT_CREATED } from "@redotech/redo-return-ui/flows/return-flow/condition-types/merchant-created";
import { MULTIPLE_CHOICE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/multiple-choice";
import { NON_RETURNED_VALUE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/non-returned-value";
import { NUM_CUSTOMER_CLAIMS } from "@redotech/redo-return-ui/flows/return-flow/condition-types/num-customer-claims";
import { NUM_CUSTOMER_RETURNS } from "@redotech/redo-return-ui/flows/return-flow/condition-types/num-customer-returns";
import { NUM_ORDERS } from "@redotech/redo-return-ui/flows/return-flow/condition-types/num-orders";
import { ORDER_NUMBER } from "@redotech/redo-return-ui/flows/return-flow/condition-types/order-number";
import { ORDER_TAG } from "@redotech/redo-return-ui/flows/return-flow/condition-types/order-tag";
import { ORDER_TIME_COLLECTION } from "@redotech/redo-return-ui/flows/return-flow/condition-types/order-time-collections";
import { ORDER_WITHIN } from "@redotech/redo-return-ui/flows/return-flow/condition-types/order-within";
import { ORDERLESS } from "@redotech/redo-return-ui/flows/return-flow/condition-types/orderless";
import { PACKAGE_PROTECTION } from "@redotech/redo-return-ui/flows/return-flow/condition-types/package-protection";
import { PAYMENT_METHOD } from "@redotech/redo-return-ui/flows/return-flow/condition-types/payment-method";
import { PLATFORM } from "@redotech/redo-return-ui/flows/return-flow/condition-types/platform";
import { PRICE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/price";
import { PRODUCT_PROPERTY } from "@redotech/redo-return-ui/flows/return-flow/condition-types/product-property";
import { PRODUCT_TAG } from "@redotech/redo-return-ui/flows/return-flow/condition-types/product-tag";
import { PRODUCT_TYPE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/product-type";
import { PRODUCT_VARIANT_METAFIELD } from "@redotech/redo-return-ui/flows/return-flow/condition-types/product-variant-metafield";
import { PURCHASE_CHANNEL } from "@redotech/redo-return-ui/flows/return-flow/condition-types/purchase-channel";
import { RETURN_METHOD } from "@redotech/redo-return-ui/flows/return-flow/condition-types/return-method";
import { RETURN_RATE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/return-rate";
import { RETURN_REASON } from "@redotech/redo-return-ui/flows/return-flow/condition-types/return-reason";
import { RETURN_TYPE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/return-type";
import { RETURN_VALUE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/return-value";
import { SALES_CHANNEL } from "@redotech/redo-return-ui/flows/return-flow/condition-types/sales-channel";
import { SKU } from "@redotech/redo-return-ui/flows/return-flow/condition-types/sku";
import { TOTAL_PRE_DISCOUNT_PRICE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/total-pre-discount-price";
import { TOTAL_RETURN_VALUE } from "@redotech/redo-return-ui/flows/return-flow/condition-types/total-return-value";
import { VENDOR } from "@redotech/redo-return-ui/flows/return-flow/condition-types/vendor";
import { WRONG_PRODUCT } from "@redotech/redo-return-ui/flows/return-flow/condition-types/wrong-product";
import { ASSIGNMENT_TYPE } from "@redotech/redo-return-ui/flows/rules/conditions/assignment-type";
import { MESSAGE_BODY_MATCHES } from "@redotech/redo-return-ui/flows/rules/conditions/message-body-matches";
import { SENT_FROM_EMAIL } from "@redotech/redo-return-ui/flows/rules/conditions/sent-from-email";
import { SUBJECT_LINE } from "@redotech/redo-return-ui/flows/rules/conditions/subject-line";
import { RECEIVED_AT_EMAIL } from "@redotech/redo-return-ui/flows/rules/received-at-email";
import { TICKET_CHANNEL } from "@redotech/redo-return-ui/flows/rules/ticket-channel";
import { TOPIC } from "@redotech/redo-return-ui/flows/rules/topic";
import { StepTypeDetailsProps } from "@redotech/redo-return-ui/flows/step";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { ButtonSize, IconButton } from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import TrashIcon from "@redotech/redo-web/icon-old/trash.svg";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { createContext, memo, useMemo } from "react";
import {
  MultipleChoiceQuestion,
  parseFlow,
} from "../../return/claim-return-shared";
import { AssignmentTypeDetails } from "../rules/conditions/assignment-type";
import { MessageBodyMatchesDetails } from "../rules/conditions/message-body-matches";
import { SentFromEmailDetails } from "../rules/conditions/sent-from-email";
import { SubjectLineDetails } from "../rules/conditions/subject-line";
import { ReceivedAtEmailDetails } from "../rules/received-at-email";
import { TicketChannelDetails } from "../rules/ticket-channel";
import { TopicDetails } from "../rules/topic";
import { AdjustmentAmountDetails } from "./condition-types/adjustment-amount";
import { AfterHoursDetails } from "./condition-types/after-hours";
import { ChannelDetails } from "./condition-types/channel";
import { CollectionDetails } from "./condition-types/collection";
import { CompensationMethodDetails } from "./condition-types/compensation-method";
import { CoverageDetails } from "./condition-types/coverage";
import { CustomerAddressDetails } from "./condition-types/customer-address";
import { CustomerCountryDetails } from "./condition-types/customer-country";
import { CustomerEmailDetails } from "./condition-types/customer-email";
import { CustomerEmailSubscriberDetails } from "./condition-types/customer-email-subscriber";
import { CustomerSmsSubscriberDetails } from "./condition-types/customer-sms-subscriber";
import { CustomerTagDetails } from "./condition-types/customer-tag";
import { DamagedDetails } from "./condition-types/damaged";
import { DateConditionDetails } from "./condition-types/date-conditions";
import { DeliveryWithinDetails } from "./condition-types/delivery-within";
import { DiscountDetails } from "./condition-types/discount";
import { ExchangeCountDetails } from "./condition-types/exchange-count";
import { ExtendedWarrantyDetails } from "./condition-types/extended-warranty";
import { ExtendedWarrantyPurchasedDetails } from "./condition-types/extended-warranty-purchased";
import { FinalSaleReturnDetails } from "./condition-types/final-sale-return";
import { FulfillmentTrackingUpdateWithinDetails } from "./condition-types/fulfillment-tracking-update-within";
import { FulfillmentWithinDetails } from "./condition-types/fulfillment-within";
import { IsRepairDetails } from "./condition-types/is-repair";
import { IsRefundReturnDetails } from "./condition-types/is_refund_return";
import { ItemDiscountDetails } from "./condition-types/item-discount";
import { ItemsBeingReturnedCountDetails } from "./condition-types/items-being-returned-count";
import { LocationNameDetails } from "./condition-types/location-name";
import { LoyaltyEarnedDetails } from "./condition-types/loyalty-earned";
import { MerchantCreatedDetails } from "./condition-types/merchant-created";
import { MultipleChoiceDetails } from "./condition-types/multiple-choice";
import { NonReturnedValueDetails } from "./condition-types/non-returned-value";
import { NumCustomerClaimsDetails } from "./condition-types/num-customer-claims";
import { NumCustomerReturnsDetails } from "./condition-types/num-customer-returns";
import { NumOrdersDetails } from "./condition-types/num-orders";
import { OrderCountGreaterThanDetails } from "./condition-types/order-count_greater_than";
import { OrderNumberDetails } from "./condition-types/order-number";
import { OrderTagDetails } from "./condition-types/order-tag";
import { OrderWithinDetails } from "./condition-types/order-within";
import { OrderlessDetails } from "./condition-types/orderless";
import { PackageProtectionDetails } from "./condition-types/package-protection";
import { PaymentMethodDetails } from "./condition-types/payment-method";
import { PlatformDetails } from "./condition-types/platform";
import { PriceDetails } from "./condition-types/price";
import { ProductPropertyDetails } from "./condition-types/product-property";
import { ProductTagDetails } from "./condition-types/product-tag";
import { ProductTypeDetails } from "./condition-types/product-type";
import { ProductVariantMetafieldDetails } from "./condition-types/product-variant-metafield";
import { PurchaseChannelDetails } from "./condition-types/purchase-channel";
import { ReturnMethodDetails } from "./condition-types/return-method";
import { ReturnRateDetails } from "./condition-types/return-rate";
import { ReturnReasonDetails } from "./condition-types/return-reason";
import { ReturnTypeDetails } from "./condition-types/return-type";
import { ReturnValueDetails } from "./condition-types/return-value";
import { SalesChannelDetails } from "./condition-types/sales-channel";
import { SkuDetails } from "./condition-types/sku";
import { TotalPreDiscountPriceDetails } from "./condition-types/total-pre-discount-price";
import { TotalReturnValueDetails } from "./condition-types/total-return-value";
import { TotalSpentGreaterThanDetails } from "./condition-types/total-spent_greater_than";
import { TrackingTypeDetails } from "./condition-types/tracking-type";
import { VendorDetails } from "./condition-types/vendor";
import { WrongProductDetails } from "./condition-types/wrong-product";

export interface FlowOptionsContextValue {
  returnReasonOptions: string[];
  claimReasonOptions: string[];
  multipleChoiceQuestions: MultipleChoiceQuestion[];
}

export const FlowOptionsContext = createContext<
  FlowOptionsContextValue | undefined
>(undefined);

// Alphabetized by display name
const returnFlowOptions: ConditionType<any, any>[] = [
  CHANNEL(ChannelDetails),
  COLLECTION(CollectionDetails),
  PACKAGE_PROTECTION(PackageProtectionDetails),
  CUSTOMER_COUNTRY(CustomerCountryDetails),
  CUSTOMER_EMAIL(CustomerEmailDetails),
  CUSTOMER_TAG(CustomerTagDetails),
  RETURN_RATE(ReturnRateDetails),
  DISCOUNT(DiscountDetails),
  ITEM_DISCOUNT(ItemDiscountDetails),
  LOYALTY_EARNED(LoyaltyEarnedDetails),
  MERCHANT_CREATED(MerchantCreatedDetails),
  EXCHANGE_COUNT(ExchangeCountDetails),
  NUM_ORDERS(NumOrdersDetails),
  NUM_CUSTOMER_CLAIMS(NumCustomerClaimsDetails),
  NUM_CUSTOMER_RETURNS(NumCustomerReturnsDetails),
  ORDER_DATE(DateConditionDetails),
  ORDER_NUMBER(OrderNumberDetails),
  ORDER_TAG(OrderTagDetails),
  ORDERLESS(OrderlessDetails),
  PAYMENT_METHOD(PaymentMethodDetails),
  PLATFORM(PlatformDetails),
  PRICE(PriceDetails),
  PRODUCT_PROPERTY(ProductPropertyDetails),
  PRODUCT_TAG(ProductTagDetails),
  PRODUCT_TYPE(ProductTypeDetails),
  PRODUCT_VARIANT_METAFIELD(ProductVariantMetafieldDetails),
  RETURN_DATE(DateConditionDetails),
  COVERAGE(CoverageDetails),
  SALES_CHANNEL(SalesChannelDetails),
  SKU(SkuDetails),
  DELIVERY_WITHIN(DeliveryWithinDetails),
  FULFILLMENT_WITHIN(FulfillmentWithinDetails),
  ORDER_WITHIN(OrderWithinDetails),
  VENDOR(VendorDetails),
];

// Alphabetized by display name
const finalizeReturnFlowOptions: ConditionType<any, any>[] = [
  CHANNEL(ChannelDetails),
  COLLECTION(CollectionDetails),
  PACKAGE_PROTECTION(PackageProtectionDetails),
  CUSTOMER_COUNTRY(CustomerCountryDetails),
  CUSTOMER_EMAIL(CustomerEmailDetails),
  CUSTOMER_TAG(CustomerTagDetails),
  RETURN_RATE(ReturnRateDetails),
  DAMAGED(DamagedDetails),
  DISCOUNT(DiscountDetails),
  MERCHANT_CREATED(MerchantCreatedDetails),
  NON_RETURNED_VALUE(NonReturnedValueDetails),
  EXCHANGE_COUNT(ExchangeCountDetails),
  ITEMS_BEING_RETURNED_COUNT(ItemsBeingReturnedCountDetails),
  NUM_ORDERS(NumOrdersDetails),
  NUM_CUSTOMER_CLAIMS(NumCustomerClaimsDetails),
  NUM_CUSTOMER_RETURNS(NumCustomerReturnsDetails),
  ORDER_DATE(DateConditionDetails),
  ORDER_NUMBER(OrderNumberDetails),
  ORDER_TAG(OrderTagDetails),
  PAYMENT_METHOD(PaymentMethodDetails),
  PLATFORM(PlatformDetails),
  PRODUCT_PROPERTY(ProductPropertyDetails),
  PRODUCT_TAG(ProductTagDetails),
  RETURN_DATE(DateConditionDetails),
  RETURN_METHOD(ReturnMethodDetails),
  RETURN_REASON(ReturnReasonDetails),
  COVERAGE(CoverageDetails),
  SALES_CHANNEL(SalesChannelDetails),
  SKU(SkuDetails),
  DELIVERY_WITHIN(DeliveryWithinDetails),
  FULFILLMENT_WITHIN(FulfillmentWithinDetails),
  ORDER_WITHIN(OrderWithinDetails),
  TOTAL_RETURN_VALUE(TotalReturnValueDetails),
  ADJUSTMENT_AMOUNT(AdjustmentAmountDetails),
  TOTAL_PRE_DISCOUNT_PRICE(TotalPreDiscountPriceDetails),
  WRONG_PRODUCT(WrongProductDetails),
];

// Alphabetized by display name
const finalizeClaimFlowOptions: ConditionType<any, any>[] = [
  COLLECTION(CollectionDetails),
  COMPENSATION_METHOD(CompensationMethodDetails),
  PACKAGE_PROTECTION(PackageProtectionDetails),
  CUSTOMER_COUNTRY(CustomerCountryDetails),
  CUSTOMER_EMAIL(CustomerEmailDetails),
  CUSTOMER_TAG(CustomerTagDetails),
  RETURN_RATE(ReturnRateDetails),
  DISCOUNT(DiscountDetails),
  MERCHANT_CREATED(MerchantCreatedDetails),
  EXCHANGE_COUNT(ExchangeCountDetails),
  NUM_ORDERS(NumOrdersDetails),
  NUM_CUSTOMER_CLAIMS(NumCustomerClaimsDetails),
  NUM_CUSTOMER_RETURNS(NumCustomerReturnsDetails),
  ORDER_DATE(DateConditionDetails),
  ORDER_NUMBER(OrderNumberDetails),
  ORDER_TAG(OrderTagDetails),
  PAYMENT_METHOD(PaymentMethodDetails),
  PLATFORM(PlatformDetails),
  PRODUCT_PROPERTY(ProductPropertyDetails),
  PRODUCT_TAG(ProductTagDetails),
  RETURN_DATE(DateConditionDetails),
  COVERAGE(CoverageDetails),
  SALES_CHANNEL(SalesChannelDetails),
  SKU(SkuDetails),
  DELIVERY_WITHIN(DeliveryWithinDetails),
  FULFILLMENT_WITHIN(FulfillmentWithinDetails),
  ORDER_WITHIN(OrderWithinDetails),
  TOTAL_RETURN_VALUE(TotalReturnValueDetails),
  ADJUSTMENT_AMOUNT(AdjustmentAmountDetails),
  TOTAL_PRE_DISCOUNT_PRICE(TotalPreDiscountPriceDetails),
];

// Alphabetized by display name
const claimFlowOptions: ConditionType<any, any>[] = [
  COLLECTION(CollectionDetails),
  PACKAGE_PROTECTION(PackageProtectionDetails),
  CUSTOMER_COUNTRY(CustomerCountryDetails),
  CUSTOMER_EMAIL(CustomerEmailDetails),
  CUSTOMER_TAG(CustomerTagDetails),
  RETURN_RATE(ReturnRateDetails),
  DISCOUNT(DiscountDetails),
  RETURN_VALUE(ReturnValueDetails),
  ITEM_DISCOUNT(ItemDiscountDetails),
  LOYALTY_EARNED(LoyaltyEarnedDetails),
  MERCHANT_CREATED(MerchantCreatedDetails),
  EXCHANGE_COUNT(ExchangeCountDetails),
  NUM_ORDERS(NumOrdersDetails),
  NUM_CUSTOMER_CLAIMS(NumCustomerClaimsDetails),
  NUM_CUSTOMER_RETURNS(NumCustomerReturnsDetails),
  ORDER_DATE(DateConditionDetails),
  ORDER_NUMBER(OrderNumberDetails),
  ORDER_TAG(OrderTagDetails),
  ORDERLESS(OrderlessDetails),
  PAYMENT_METHOD(PaymentMethodDetails),
  PLATFORM(PlatformDetails),
  PRICE(PriceDetails),
  PRODUCT_PROPERTY(ProductPropertyDetails),
  PRODUCT_TAG(ProductTagDetails),
  PRODUCT_TYPE(ProductTypeDetails),
  PRODUCT_VARIANT_METAFIELD(ProductVariantMetafieldDetails),
  RETURN_DATE(DateConditionDetails),
  COVERAGE(CoverageDetails),
  SALES_CHANNEL(SalesChannelDetails),
  SKU(SkuDetails),
  DELIVERY_WITHIN(DeliveryWithinDetails),
  FULFILLMENT_WITHIN(FulfillmentWithinDetails),
  FULFILLMENT_TRACKING_UPDATE_WITHIN(FulfillmentTrackingUpdateWithinDetails),
  ORDER_WITHIN(OrderWithinDetails),
  VENDOR(VendorDetails),
];

// Alphabetized by display name
const emailFlowOptions: ConditionType<any, any>[] = [
  COLLECTION(CollectionDetails),
  CUSTOMER_COUNTRY(CustomerCountryDetails),
  CUSTOMER_EMAIL_SUBSCRIBER(CustomerEmailSubscriberDetails),
  CUSTOMER_SMS_SUBSCRIBER(CustomerSmsSubscriberDetails),
  ORDER_TAG(OrderTagDetails),
  PRODUCT_PROPERTY(ProductPropertyDetails),
  PRODUCT_TAG(ProductTagDetails),
  SKU(SkuDetails),
];

const ruleFlowOptions: ConditionType<any, any>[] = [
  ASSIGNMENT_TYPE(AssignmentTypeDetails),
  AFTER_HOURS(AfterHoursDetails),
  RECEIVED_AT_EMAIL(ReceivedAtEmailDetails),
  SENT_FROM_EMAIL(SentFromEmailDetails),
  MESSAGE_BODY_MATCHES(MessageBodyMatchesDetails),
  TOPIC(TopicDetails),
  SUBJECT_LINE(SubjectLineDetails),
  TICKET_CHANNEL(TicketChannelDetails),
];

const warrantyFlowOptions: ConditionType<any, any>[] = [
  ...claimFlowOptions,
  EXTENDED_WARRANTY(ExtendedWarrantyDetails),
  EXTENDED_WARRANTY_PURCHASED(ExtendedWarrantyPurchasedDetails),
];

const registrationFlowOptions: ConditionType<any, any>[] = [
  COLLECTION(CollectionDetails),
  CUSTOMER_COUNTRY(CustomerCountryDetails),
  CUSTOMER_EMAIL(CustomerEmailDetails),
  CUSTOMER_TAG(CustomerTagDetails),
  ORDER_NUMBER(OrderNumberDetails),
  ORDER_TAG(OrderTagDetails),
  PRODUCT_PROPERTY(ProductPropertyDetails),
  PRODUCT_TAG(ProductTagDetails),
  PRODUCT_TYPE(ProductTypeDetails),
  PURCHASE_CHANNEL(PurchaseChannelDetails),
  RETURN_REASON(ReturnReasonDetails),
  SKU(SkuDetails),
];

// Alphabetized by display name
const chatFlowOptions: ConditionType<any, any>[] = [
  AFTER_HOURS(AfterHoursDetails),
];

// Alphabetized by display name
const locationFlowOptions: ConditionType<any, any>[] = [
  COLLECTION(CollectionDetails),
  CUSTOMER_ADDRESS(CustomerAddressDetails),
  CUSTOMER_COUNTRY(CustomerCountryDetails),
  CUSTOMER_EMAIL(CustomerEmailDetails),
  CUSTOMER_TAG(CustomerTagDetails),
  DAMAGED(DamagedDetails),
  DISCOUNT(DiscountDetails),
  FINAL_SALE_RETURN(FinalSaleReturnDetails),
  IS_REPAIR(IsRepairDetails),
  MULTIPLE_CHOICE(MultipleChoiceDetails),
  ORDER_NUMBER(OrderNumberDetails),
  ORDER_TAG(OrderTagDetails),
  LOCATION_NAME(LocationNameDetails),
  PRICE(PriceDetails),
  PRODUCT_PROPERTY(ProductPropertyDetails),
  PRODUCT_TAG(ProductTagDetails),
  PRODUCT_VARIANT_METAFIELD(ProductVariantMetafieldDetails),
  RETURN_REASON(ReturnReasonDetails),
  RETURN_TYPE(ReturnTypeDetails),
  SALES_CHANNEL(SalesChannelDetails),
  SKU(SkuDetails),
  VENDOR(VendorDetails),
];

const labelsFlowOptions: ConditionType<any, any>[] = [
  COLLECTION(CollectionDetails),
  PRODUCT_TAG(ProductTagDetails),
  PRODUCT_TYPE(ProductTypeDetails),
  SKU(SkuDetails),
];

const returnProcessingFlowOptions: ConditionType<any, any>[] = [];

const flowTypeToOptions = {
  [FlowType.CHAT]: chatFlowOptions,
  [FlowType.CLAIM]: claimFlowOptions,
  [FlowType.FINALIZE_RETURN]: finalizeReturnFlowOptions,
  [FlowType.FINALIZE_CLAIM]: finalizeClaimFlowOptions,
  [FlowType.EMAIL]: emailFlowOptions,
  [FlowType.RULE]: ruleFlowOptions,
  [FlowType.RETURN]: returnFlowOptions,
  [FlowType.WARRANTY]: warrantyFlowOptions,
  [FlowType.WARRANTY_REGISTRATION]: registrationFlowOptions,
  [FlowType.RECYCLING]: claimFlowOptions,
  [FlowType.RETURN_LOCATION]: locationFlowOptions,
  [FlowType.LABELS]: labelsFlowOptions,
  [FlowType.RETURN_PROCESSING]: returnProcessingFlowOptions,
};

export const ConditionDetails = memo(function ConditionDetails({
  stepSelect,
  state,
  setState,
  flowType,
  onDeleteChild,
}: StepTypeDetailsProps<ConditionState>) {
  const team = useRequiredContext(TeamContext);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const setConditionState = useHandler((conditionState: any) => {
    setState((state) => ({ ...state, conditionState }));
  });
  const handleType = useHandler(
    async (conditionType: ConditionType<any, any>) => {
      setState((state) => ({
        ...state,
        conditionType,
        conditionState: conditionType!.empty,
      }));
    },
  );

  const flowOptions = useMemo((): FlowOptionsContextValue => {
    const returnFlowParsed = parseFlow(
      team?.settings.returnFlow as ReturnFlow | undefined,
    );
    const claimFlowParsed = parseFlow(
      team?.settings.claimFlow as ReturnFlow | undefined,
    );

    return {
      returnReasonOptions: returnFlowParsed.reasonOptions,
      claimReasonOptions: claimFlowParsed.reasonOptions,
      multipleChoiceQuestions: [
        ...returnFlowParsed.multipleChoiceQuestions,
        ...claimFlowParsed.multipleChoiceQuestions,
      ],
    };
  }, [team?.settings.returnFlow, team?.settings.claimFlow]);
  const flowTypeOptions = useMemo(() => {
    const defaultOptions = flowTypeToOptions[flowType];
    if (
      team?.settings.returns?.finalSaleReturns?.enabled &&
      flowType === FlowType.RETURN
    ) {
      defaultOptions.push(FINAL_SALE_RETURN(FinalSaleReturnDetails));
    }
    // Add ORDER_TIME_COLLECTION condition only for BigCommerce teams
    if (team?.platform === "bigcommerce") {
      const flowsWithOrderTimeCollection = [
        FlowType.RETURN,
        FlowType.FINALIZE_RETURN,
        FlowType.FINALIZE_CLAIM,
        FlowType.CLAIM,
        FlowType.WARRANTY,
      ];

      if (flowsWithOrderTimeCollection.includes(flowType)) {
        defaultOptions.push(ORDER_TIME_COLLECTION(CollectionDetails));
      }
    }

    return defaultOptions;
  }, [
    flowType,
    team?.settings.returns?.finalSaleReturns?.enabled,
    team?.platform,
  ]);

  const conditionOptions: RedoListItem<ConditionType<any, any>>[] =
    useMemo(() => {
      return flowTypeOptions.map((conditionType) => ({
        id: conditionType.name,
        value: conditionType,
        type: "text" as const,
        text: conditionType.name,
      }));
    }, [flowTypeOptions]);

  const selectedCondition = state.conditionType
    ? conditionOptions.find(
        (option) => option.value.name === state.conditionType?.name,
      )
    : undefined;

  return (
    <>
      <Flex align="center" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          Condition
        </Text>
        {onDeleteChild && (
          <IconButton onClick={onDeleteChild} size={ButtonSize.SMALL}>
            <TrashIcon />
          </IconButton>
        )}
      </Flex>
      <RedoSingleSelectDropdownInput
        disabled={!canEditReturnsSettings}
        flexGrow
        options={conditionOptions}
        optionSelected={({ item }) => handleType(item.value)}
        placeholder="Select condition..."
        selectedItem={selectedCondition}
        size="xs"
      />
      {state.conditionType?.description(state.conditionState) && (
        <Text fontSize="xxs" textColor="tertiary">
          {state.conditionType.description(state.conditionState)}
        </Text>
      )}

      {state.conditionType && (
        <FlowOptionsContext.Provider value={flowOptions}>
          <state.conditionType.Details
            disabled={!canEditReturnsSettings}
            flowType={flowType}
            setState={setConditionState}
            state={state.conditionState}
          />
        </FlowOptionsContext.Provider>
      )}
      {flowType !== FlowType.RULE && (
        <>
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              If matches, then
            </Text>
            {stepSelect({
              value: state.nextTrue,
              handleIndex: 0,
              valueChange: (nextTrue) =>
                setState((state) => ({ ...state, nextTrue })),
            })}
            {flowType !== FlowType.FINALIZE_RETURN &&
              flowType !== FlowType.FINALIZE_CLAIM &&
              !state.nextTrue && (
                <Text fontSize="xs" textColor="error">
                  Required
                </Text>
              )}
          </Flex>
          <Flex dir="column" gap="xs">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              If does not match, then
            </Text>
            {stepSelect({
              value: state.nextFalse,
              handleIndex: 1,
              valueChange: (nextFalse) =>
                setState((state) => ({ ...state, nextFalse })),
            })}
            {flowType !== FlowType.FINALIZE_RETURN &&
              flowType !== FlowType.FINALIZE_CLAIM &&
              !state.nextFalse && (
                <Text fontSize="xs" textColor="error">
                  Required
                </Text>
              )}
          </Flex>
        </>
      )}
    </>
  );
});

export const getDetailsForCondition = (
  condition: Condition,
): React.ComponentType<ConditionTypeDetailsProps<any>> => {
  switch (condition.type) {
    case Condition.COLLECTION:
      return CollectionDetails;
    case Condition.ORDER_TIME_COLLECTION:
      return CollectionDetails;
    case Condition.COVERAGE:
      return CoverageDetails;
    case Condition.PACKAGE_PROTECTION:
      return PackageProtectionDetails;
    case Condition.CUSTOMER_COUNTRY:
      return CustomerCountryDetails;
    case Condition.CUSTOMER_ADDRESS:
      return CustomerAddressDetails;
    case Condition.CUSTOMER_TAG:
      return CustomerTagDetails;
    case Condition.CUSTOMER_EMAIL:
      return CustomerEmailDetails;
    case Condition.ORDER_NUMBER:
      return OrderNumberDetails;
    case Condition.DISCOUNT:
      return DiscountDetails;
    case Condition.ITEM_DISCOUNT:
      return ItemDiscountDetails;
    case Condition.LOYALTY_EARNED:
      return LoyaltyEarnedDetails;
    case Condition.EXCHANGE_COUNT:
      return ExchangeCountDetails;
    case Condition.ITEMS_BEING_RETURNED_COUNT:
      return ItemsBeingReturnedCountDetails;
    case Condition.ADJUSTMENT_AMOUNT:
      return AdjustmentAmountDetails;
    case Condition.RETURN_METHOD:
      return ReturnMethodDetails;
    case Condition.NUM_CUSTOMER_CLAIMS:
      return NumCustomerClaimsDetails;
    case Condition.NUM_CUSTOMER_RETURNS:
      return NumCustomerReturnsDetails;
    case Condition.NUM_ORDERS:
      return NumOrdersDetails;
    case Condition.EXTENDED_WARRANTY:
      return ExtendedWarrantyDetails;
    case Condition.EXTENDED_WARRANTY_PURCHASED:
      return ExtendedWarrantyPurchasedDetails;
    case Condition.RETURN_RATE:
      return ReturnRateDetails;
    case Condition.ORDER_DATE:
      return DateConditionDetails;
    case Condition.ORDER_TAG:
      return OrderTagDetails;
    case Condition.PAYMENT_METHOD:
      return PaymentMethodDetails;
    case Condition.PLATFORM:
      return PlatformDetails;
    case Condition.PRICE:
      return PriceDetails;
    case Condition.RETURN_VALUE:
      return ReturnValueDetails;
    case Condition.NON_RETURNED_VALUE:
      return NonReturnedValueDetails;
    case Condition.PRODUCT_PROPERTY:
      return ProductPropertyDetails;
    case Condition.PRODUCT_TAG:
      return ProductTagDetails;
    case Condition.PRODUCT_TYPE:
      return ProductTypeDetails;
    case Condition.PRODUCT_VARIANT_METAFIELD:
      return ProductVariantMetafieldDetails;
    case Condition.RETURN_DATE:
      return DateConditionDetails;
    case Condition.SALES_CHANNEL:
      return SalesChannelDetails;
    case Condition.SKU:
      return SkuDetails;
    case Condition.DELIVERY_WITHIN:
      return DeliveryWithinDetails;
    case Condition.FULFILLMENT_WITHIN:
      return FulfillmentWithinDetails;
    case Condition.FINAL_SALE_RETURN:
      return FinalSaleReturnDetails;
    case Condition.ORDERLESS:
      return OrderlessDetails;
    case Condition.ORDER_WITHIN:
      return OrderWithinDetails;
    case Condition.AFTER_HOURS:
      return AfterHoursDetails;
    case Condition.FULFILLMENT_TRACKING_UPDATE_WITHIN:
      return FulfillmentTrackingUpdateWithinDetails;
    case Condition.COMPENSATION_METHOD:
      return CompensationMethodDetails;
    case Condition.CUSTOMER_EMAIL_SUBSCRIBER:
      return CustomerEmailSubscriberDetails;
    case Condition.CUSTOMER_SMS_SUBSCRIBER:
      return CustomerSmsSubscriberDetails;
    case Condition.TICKET_CHANNEL:
      return TicketChannelDetails;
    case Condition.TOPIC:
      return TopicDetails;
    case Condition.RECEIVED_AT_EMAIL:
      return ReceivedAtEmailDetails;
    case Condition.SENT_FROM_EMAIL:
      return SentFromEmailDetails;
    case Condition.SUBJECT_LINE:
      return SubjectLineDetails;
    case Condition.MESSAGE_BODY_MATCHES:
      return MessageBodyMatchesDetails;
    case Condition.ORDER_COUNT_GREATER_THAN:
      return OrderCountGreaterThanDetails;
    case Condition.TOTAL_SPENT_GREATER_THAN:
      return TotalSpentGreaterThanDetails;
    case Condition.VENDOR:
      return VendorDetails;
    case Condition.TRACKING_TYPE:
      return TrackingTypeDetails;
    case Condition.IS_REFUND_RETURN:
      return IsRefundReturnDetails;
    case Condition.PURCHASE_CHANNEL:
      return PurchaseChannelDetails;
    case Condition.DAMAGED:
      return DamagedDetails;
    case Condition.WRONG_PRODUCT:
      return WrongProductDetails;
    case Condition.RETURN_REASON:
      return ReturnReasonDetails;

    case Condition.ASSIGNMENT_TYPE:
      return AssignmentTypeDetails;
    case Condition.CHANNEL:
      return ChannelDetails;
    case Condition.MERCHANT_CREATED:
      return MerchantCreatedDetails;
    case Condition.TOTAL_RETURN_VALUE:
      return TotalReturnValueDetails;
    case Condition.TOTAL_PRE_DISCOUNT_PRICE:
      return TotalPreDiscountPriceDetails;
    case Condition.RETURN_TYPE:
      return ReturnTypeDetails;
    case Condition.IS_REPAIR:
      return IsRepairDetails;
    case Condition.LOCATION_NAME:
      return LocationNameDetails;
    case Condition.MULTIPLE_CHOICE:
      return MultipleChoiceDetails;
    default:
      assertNever(condition);
  }
};
